import React from 'react';
import { useTranslation } from 'react-i18next';

import LanguageSwitcher from 'shared/components/LanguageSwitcher';
import { HAAT_LOGO_URL, PARTNER_CONNECT_HERO_URL } from 'shared/constants/assets';
import { useLanguage } from 'shared/context/LanguageContext';
import { ScrollAreaProvider } from 'shared/context/ScrollAreaContext';
import StepFooter from 'features/onboarding/components/StepFooter';

interface OnboardingSplitLayoutProps {
  /** The linear progress bar rendered at the top of the form column. */
  progressBar: React.ReactNode;
  /**
   * Bound to the scrolling form column and shared with the subtree, so steps
   * and the footer scroll the right element instead of the window.
   */
  scrollAreaRef: React.RefObject<HTMLElement>;
  /** The current step form (already wrapped in its own transition). */
  children: React.ReactNode;
}

/**
 * Two-panel onboarding layout: a bold branded showcase panel on the LEFT and
 * the scrollable form column on the RIGHT.
 *
 * The outer container is forced to `dir="ltr"` so the visual placement
 * (image left / form right) is identical in RTL and LTR languages. Each panel
 * restores the user's reading direction on its own content wrapper so Hebrew /
 * Arabic text and form fields still render correctly.
 */
const OnboardingSplitLayout: React.FC<OnboardingSplitLayoutProps> = ({
  progressBar,
  scrollAreaRef,
  children,
}) => {
  const { t } = useTranslation();
  const { dir } = useLanguage();

  return (
    <ScrollAreaProvider containerRef={scrollAreaRef}>
      <div dir="ltr" className="flex h-screen w-full overflow-hidden bg-white">
        {/* ---------------------------------------------------------------- */}
        {/* LEFT — branded showcase panel (hidden on small screens)          */}
        {/* ---------------------------------------------------------------- */}
        <aside className="hidden h-screen w-1/2 shrink-0 overflow-hidden bg-gray-100 lg:flex">
          <img
            src={PARTNER_CONNECT_HERO_URL}
            alt={t('landingSections.banner.alt.hero') as string}
            className="h-full w-full object-cover"
            loading="eager"
            decoding="async"
          />
        </aside>

        {/* ---------------------------------------------------------------- */}
        {/* RIGHT — form column                                              */}
        {/* ---------------------------------------------------------------- */}
        <div className="relative flex h-screen flex-1 flex-col overflow-hidden bg-white">
          {/* Frosted-glass header: logo on the left, languages on the right.
              Kept in the outer dir="ltr" context so the visual placement is
              identical in RTL and LTR languages. */}
          <header className="relative z-30 shrink-0 border-b border-gray-300 bg-white/75 shadow-[0_1px_12px_rgba(0,0,0,0.04)] backdrop-blur-xl">
            <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-8">
              <div className="flex items-center gap-3">
                <img
                  src={HAAT_LOGO_URL}
                  alt={t('appName', 'HAAT') ?? 'HAAT'}
                  className="h-12 w-auto transition-transform duration-300 hover:scale-105"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <LanguageSwitcher variant="header" />
            </div>
          </header>

          <div dir={dir} className="relative z-10 flex min-h-0 flex-1 flex-col">
            {/* Progress bar (fixed, non-scrolling) */}
            <div className="shrink-0 w-full px-5 pt-6 sm:px-8">
              {progressBar}
            </div>

            {/* Form content — the only scrollable region, so the page itself
                stays fixed and long forms scroll internally. */}
            <main
              ref={scrollAreaRef}
              className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8"
            >
              <div className="w-full">{children}</div>
            </main>

            {/* Fixed footer with the Back / Next controls for the current step */}
            <StepFooter />
          </div>
        </div>
      </div>
    </ScrollAreaProvider>
  );
};

export default OnboardingSplitLayout;
